import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MessageCircle, Mail, DollarSign, TrendingUp } from "lucide-react";
import MetricCard from "../components/MetricCard";
import CreditBillingTable from "../components/CreditBillingTable";
import { APIURL } from "../../Global";
import "../styles.css";

const formatClinicName = (name) => {
  if (!name) return "—";
  return name
    .toString()
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ");
};

export const BillingPanel = () => {
  const [cardStats, setCardStats] = useState(null);
  const [creditUsage, setCreditUsage] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const fetchCreditData = async () => {
      setIsLoading(true);
      setApiError(null);
      try {
        const tokenString = sessionStorage.getItem("usertoken");
        const token = tokenString ? tokenString.replace(/["]+/g, "") : null;
        const headers = token ? { Authorization: `Token ${token}` } : {};
        const { data } = await axios.get(`${APIURL}/api/v1/service-provider/central-credits/`, {
          headers,
        });

        setCardStats(data?.cards || null);
        const detail = Array.isArray(data?.credit_detail) ? data.credit_detail : [];
        const formatted = detail.flatMap((clinic) => {
          const channels = clinic.channels || [];
          const clinicName = formatClinicName(clinic.hospital_name);
          return channels.map((channel) => {
            const totalCredit = channel.total_credit ?? 0;
            const usedCredits =
              channel.used_credits ?? channel.consumed_credit ?? 0;
            const remainingCredits =
              channel.remaining_credits ?? Math.max(totalCredit - usedCredits, 0);
            return {
              id: `${clinic.hospital_id}-${channel.channel_type || "channel"}`,
              clinicId: clinic.hospital_id,
              clinicName,
              channelType: channel.channel_type || "whatsapp",
              totalCredit,
              usedCredits,
              remainingCredits,
              totalMessages: channel.total_messages ?? 0,
              usedMessages: channel.used_messages ?? 0,
              overUsedMessages: channel.over_used_messages ?? 0,
              revenue: channel.revenue ?? 0,
            };
          });
        });
        setCreditUsage(formatted);
      } catch (error) {
        console.error("Failed to load central credit data", error);
        setApiError("Unable to load credit usage. Please try again shortly.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreditData();
  }, []);

  const metrics = useMemo(
    () => ({
      totalWhatsappMessages: cardStats?.total_whatsapp_messages ?? 0,
      totalEmailMessages: cardStats?.total_email_messages ?? 0,
      whatsappCredits: cardStats?.whatsapp_total_credit ?? 0,
      emailCredits: cardStats?.email_total_credit ?? 0,
      whatsappRevenue: cardStats?.whatsapp_revenue ?? 0,
      emailRevenue: cardStats?.email_revenue ?? 0,
    }),
    [cardStats],
  );

  return (
    <>
      <div className="pharmacy-page-header">
        <h1 className="pharmacy-page-title">Communications</h1>
        <p className="pharmacy-page-subtitle">
          Centralized communications tracking with clinic-wise message breakdown
        </p>
      </div>

      <div className="pharmacy-card-grid">
        <MetricCard
          title="Total WhatsApp Messages"
          value={metrics.totalWhatsappMessages.toLocaleString()}
          icon={MessageCircle}
        //   trend="+15% from last month"
          trendUp
        />
        <MetricCard
          title="Total Email Messages"
          value={metrics.totalEmailMessages.toLocaleString()}
          icon={Mail}
        //   trend="+18% from last month"
          trendUp
        />
        <MetricCard
          title="WhatsApp Revenue"
          value={`QAR ${metrics.whatsappRevenue.toFixed(2)}`}
          icon={DollarSign}
        //   trend="+12% from last month"
          trendUp
        />
        <MetricCard
          title="Email Revenue"
          value={`QAR ${metrics.emailRevenue.toFixed(2)}`}
          icon={TrendingUp}
        //   trend="+10% from last month"
          trendUp
        />
      </div>

      {apiError && (
        <div style={{ color: "#d92d20", marginBottom: 16 }}>{apiError}</div>
      )}
      {isLoading ? (
        <div style={{ padding: "24px 0" }}>Loading credit usage...</div>
      ) : (
        <CreditBillingTable creditUsage={creditUsage} />
      )}
    </>
  );
};

const Billing = () => (
  <div className="pharmacy-page">
    <div className="pharmacy-container">
      <BillingPanel />
    </div>
  </div>
);

export default Billing;

